import React, { useState, useMemo } from "react";
import { Search } from "lucide-react";

export default function PerformanceManagersTable({ managers = [] }) {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    // Apply filtering and search
    const filteredList = useMemo(() => {
        return managers.filter(m => 
            m.managerNom?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [managers, searchTerm]);

    // Apply pagination
    const totalItems = filteredList.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const paginatedList = useMemo(() => {
        const start = (safePage - 1) * pageSize;
        return filteredList.slice(start, start + pageSize);
    }, [filteredList, safePage]);

    const formatPercent = (val) => {
        if (val === null || val === undefined) return "—";
        return `${parseFloat(val).toFixed(2)}%`;
    };

    return (
        <div>
            {/* Search Toolbar */}
            <div className="perf-cons-toolbar">
                <div className="perf-cons-search-container" style={{ flex: "1 1 100%" }}>
                    <Search size={16} className="perf-cons-search-icon" />
                    <input
                        type="text"
                        placeholder="Rechercher par manager..."
                        className="perf-cons-search-input"
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                </div>
            </div>

            {/* Table */}
            <div className="perf-table-wrap">
                <table className="perf-table">
                    <thead>
                        <tr>
                            <th>Manager</th>
                            <th>Total Collaborateurs</th>
                            <th>Répartition des Statuts</th>
                            <th>Progression Moyenne</th>
                            <th>Score Global Moyen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedList.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="perf-state" style={{ textAlign: "center", padding: "30px" }}>
                                    Aucun manager trouvé.
                                </td>
                            </tr>
                        ) : (
                            paginatedList.map((m, idx) => (
                                <tr key={m.managerId || idx}>
                                    <td style={{ fontWeight: "700", color: "#204385" }}>
                                        {m.managerNom || "Non affecté"}
                                    </td>
                                    <td><strong>{m.totalCollaborateurs}</strong></td>
                                    <td>
                                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                            <span className="status-badge NOT_STARTED" title="Non démarré">
                                                {m.notStartedCount || 0} ND
                                            </span>
                                            <span className="status-badge IN_PROGRESS" title="En cours">
                                                {m.inProgressCount || 0} EC
                                            </span>
                                            <span className="status-badge PENDING_VALIDATION" title="En attente validation">
                                                {m.pendingValidationCount || 0} EV
                                            </span>
                                            <span className="status-badge COMPLETED" title="Terminé">
                                                {m.completedCount || 0} T
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <span style={{ fontSize: "11px", fontWeight: "700", minWidth: "30px" }}>
                                                {parseFloat(m.progressionMoyenne || 0).toFixed(0)}%
                                            </span>
                                            <div className="perf-progressbar" style={{ width: "60px" }}>
                                                <span style={{ width: `${m.progressionMoyenne || 0}%` }}></span>
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{ fontWeight: "800", color: "#204385" }}>
                                        {formatPercent(m.scoreGlobalMoyen)}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="perf-cons-pagination">
                    <button 
                        className="perf-cons-page-btn" 
                        disabled={safePage === 1}
                        onClick={() => setCurrentPage(safePage - 1)}
                    >
                        Précédent
                    </button>
                    <span className="perf-cons-page-info">
                        Page <strong>{safePage}</strong> sur <strong>{totalPages}</strong> ({totalItems} managers)
                    </span>
                    <button 
                        className="perf-cons-page-btn" 
                        disabled={safePage === totalPages}
                        onClick={() => setCurrentPage(safePage + 1)}
                    >
                        Suivant
                    </button>
                </div>
            )}
        </div>
    );
}
